import * as React from "react";
import {Button, Grid, OverlayTrigger, Tooltip} from "react-bootstrap";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {ActionBar} from "../common/ui/actionbar/ActionBar";
import {connect} from "react-redux";
import {DetailHeader} from "../common/ui/detailsection/DetailHeader";
import {DetailSection, DetailSectionProps} from "../common/ui/detailsection/DetailSection";
import {DetailView, Formats} from "../common/ui/detailsection/DetailView";
import {LcdIcon} from "../common/ui/icon/LcdIcon";
import {MasterDetailLayout} from "../common/ui/layouts/MasterDetailLayout";
import {actions as notificationActions} from "../common/ui/notification/actions";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {AddToProductButton} from "../products/AddToProductButton";
import {CreateProductButton} from "../products/CreateProductButton";
import {PublishDataInServiceButton} from "../services/CreateServiceButton";
import {DataOutdatedWarning} from "./DataOutdatedWarning";
import {DeleteDataButton} from "./DeleteDataButton";
import {ImportedDataFilter} from "./ImportedDataFilter";
import {ImportedDataList} from "./ImportedDataList";
import {APOLLO_CATALOG_ROOT_FOLDER, ApolloCatalogData, ImportedData, ImportedDataSearchFilter} from "./model";
import {Accordion, AccordionDetails, AccordionSummary, Chip, withStyles} from "@material-ui/core";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {DimensionProps, Dimensions} from "../common/ui/dimensions/Dimensions";
import {DataFolderBrowser} from "./DataFolderBrowser";
import {selectors} from "./selectors";
import {actions} from "./actions";
import {CreateFolderDialog} from "./CreateFolderDialog";
import {getSelectedPath} from "./Util";
import {fullViewPortDimensions} from "../common/ui/table/ControlRoomTable";
import {AddDataDialog} from "./AddDataDialog";
import {AddResponse} from "../api/InternalApi";
import {COLUMN_SORT_SETTINGS_DATA, DATA_LIST_PAGE_OPTIONS_KEY, localRepository} from "../common/LocalRepository";
import {SortDirection, SortSettings, updateSortIndicators} from "../common/ui/table/model";

interface DataListState {
  selection: ImportedData[];
  isDataFolderBrowserOpened: boolean;
  isFolderBrowserExpanded: boolean;
  isCreateFolderDialogOpen: boolean;
  showAddDataDialog: boolean;
  sortSettings?: SortSettings | null;
}

interface DataListProps {
  filter: ImportedDataSearchFilter,
  folderId: string;
  notify: (message: string) => void;
  clearData: () => void;
  clearFilter: () => void;
  loadFolders: () => void;
  updateFolders: (folders: ApolloCatalogData[], folderId: string) => void;
  filterChanged: (filter: ImportedDataSearchFilter) => void;
}

const MESSAGES = defineMessages({
  single: {
    id: "studio.data.create-folder-single-tooltip",
    defaultMessage: "Select a single folder from the list below"
  },
  dataList: {
    id: "studio.data.create-folder-list-tooltip-data",
    defaultMessage: "Add data to the selected folder in the list below, create new OGC or Spatial Database connection"
  },
  dataFolderBrowser: {
    id: "studio.data.create-folder-folder-browser-tooltip-data",
    defaultMessage: "Add data to selected folder in the folder browser, create new OGC or Spatial Database connection"
  },
  dataRoot: {
    id: "studio.data.create-folder-root-tooltip-data",
    defaultMessage: "Add data to the ROOT folder, create new OGC or Spatial Database connection"
  },
  folderList: {
    id: "studio.data.create-folder-list-tooltip-folder",
    defaultMessage: "Add a folder to the selected folder in the list below"
  },
  folderFolderBrowser: {
    id: "studio.data.create-folder-folder-browser-tooltip-folder",
    defaultMessage: "Add a folder to selected folder in the folder browser"
  },
  folderRoot: {
    id: "studio.data.create-folder-root-tooltip-folder",
    defaultMessage: "Add a folder to the ROOT folder"
  },
  addSubmitted: {
    id: "studio.data.data-list-page.add-submitted",
    defaultMessage: "The add data request was successfully submitted. You can check the status of the add job on the Jobs page."
  },
});

const accordionSummaryStyles = {
  root: {
    minHeight: '0',
  },
  content: {
    margin: '10px 0',
    width: 'calc(100% - 40px)',
  },
  expandIcon: {
    padding: '0 12px',
  },
};
const CustomAccordionSummary = withStyles(accordionSummaryStyles)(AccordionSummary);
const accordionStyles = {
  expanded: {
    margin: '13px 0!important',
  },
};
const CustomAccordion = withStyles(accordionStyles)(Accordion);

const chipStyles: any = {
  root: {
    margin: '-6px 0 0 0',
    position: 'relative',
    top: '3px',
    flex: '1 1 0',
    overflow: 'hidden',
  },
}
const CustomChip = withStyles(chipStyles)(Chip);

class DataListDetailSectionComponent extends DetailSection<DetailSectionProps<ImportedData> & WithApiProperties & InjectedIntlProps, ImportedData> {

  loadData(props: DetailSectionProps<ImportedData>): Promise<ImportedData> {
    if (props.selection.length === 1) {
      return this.props.api.getImportedDataById(props.selection[0].id);
    }
    return Promise.resolve(null);
  }

  renderContent() {
    const {isLoading, error} = this.state;
    const {selection} = this.props;

    if (selection.length === 0) {
      return (
          <div className="emptyDetailPlaceholder">
            <FormattedMessage id="studio.data.data-list-page.select-data"
                              defaultMessage="Select some data in the table."/>
          </div>
      );
    }
    if (selection.length > 1) {
      return <p><FormattedMessage id="studio.data.data-list-page.item-length"
                                  defaultMessage="Selected {length} items."
                                  values={{length: selection.length}}/></p>;
    }
    if (selection.length === 1 && selection[0].type.toUpperCase() === "FOLDER") {
      return (
          <div className="emptyDetailPlaceholder">
            <FormattedMessage id="studio.data.data-list-page.select-data-folder"
                              defaultMessage="Folder preview not supported."/>
          </div>
      );
    }
    if (selection.length === 1 && this.state.data && !isLoading && !error) {
      const data = this.state.data;
      return (
          <div className="details">
            <DetailHeader title={data.title}/>
            <DetailView fields={[
              {
                key: "Path",
                name: <FormattedMessage id="studio.data.data-list-page.data-detail.path"
                                        defaultMessage="Path"/>,
                value: data.filePath,
              },
              {
                key: "Format",
                name: <FormattedMessage id="studio.data.data-list-page.data-detail.format"
                                        defaultMessage="Format"/>,
                value: data.type,
              },
              {
                key: "File Size",
                name: <FormattedMessage id="studio.data.data-list-page.data-detail.file-size"
                                        defaultMessage="File size"/>,
                value: data.fileMetadata.size,
                format: Formats.FILESIZE,
              },
            ]}/>
          </div>
      );
    }
    return super.renderContent();
  }
}

const DataListDetailSection = WithApi(injectIntl(DataListDetailSectionComponent));

interface DataListPageProps {
  filter: ImportedDataSearchFilter;
  folders: ApolloCatalogData[];
  clearSelection: () => void;
  filterChanged: (filter: ImportedDataSearchFilter) => void;
}

class DataListPageComponent extends React.Component<DataListProps & WithApiProperties & InjectedIntlProps & DimensionProps & DataListPageProps, DataListState> {
  constructor(props) {
    super(props);

    this.state = {
      selection: [],
      isDataFolderBrowserOpened: false,
      isFolderBrowserExpanded: false,
      isCreateFolderDialogOpen: false,
      showAddDataDialog: false,
      sortSettings: null,
    };
    this.toggleDataFolderBrowser = this.toggleDataFolderBrowser.bind(this);
  }

  componentDidMount() {
    this.reloadIfInvalidFolderParameter();
    const dataListPageOptions = localRepository.getOptions<DataListState>(DATA_LIST_PAGE_OPTIONS_KEY);
    let sortSettings: SortSettings = localRepository.getOptions<SortSettings>(COLUMN_SORT_SETTINGS_DATA);
    if (!sortSettings) {
      sortSettings = {
        column: "name",
        direction: SortDirection.asc
      }
      localRepository.setOptions(COLUMN_SORT_SETTINGS_DATA, sortSettings);
    }
    this.setState({sortSettings});

    if (dataListPageOptions?.isFolderBrowserExpanded) {
      this.setState({isFolderBrowserExpanded: dataListPageOptions.isFolderBrowserExpanded});
    }
  }

  reloadIfInvalidFolderParameter = () => {
    const urlParams = new URLSearchParams(document.URL.substring(document.URL.lastIndexOf('?')));
    if (urlParams.has('folder')) {
      const folderId = urlParams.get('folder');
      if (folderId !== APOLLO_CATALOG_ROOT_FOLDER.id) {
        this.props.api.isFolder(folderId).then((isFolder) => {
          if (!isFolder) {
            urlParams.delete('folder');
            window.history.replaceState({}, '',
                `${location.pathname}${Array.from(urlParams).length > 0 ? "?" + urlParams.toString() : ""}`
            );
            window.location.reload();
          }
        });
      }
    }
  }

  onSortChange = (columnKey: string) => {
    this.clearSelection();
    this.setState((prevState) => {
      if (prevState.sortSettings.column === columnKey) {
        const sortStates = Object.values(SortDirection);
        const currentIndex = sortStates.indexOf(this.state.sortSettings.direction);
        const nextIndex = (currentIndex + 1) % sortStates.length;
        const nextSortSettings: SortSettings = {column: columnKey, direction: sortStates[nextIndex]};
        localRepository.setOptions(COLUMN_SORT_SETTINGS_DATA, nextSortSettings);
        updateSortIndicators(nextSortSettings.column, nextSortSettings.direction, columnKey);
        return {sortSettings: nextSortSettings};
      }
      localRepository.setOptions(COLUMN_SORT_SETTINGS_DATA, {column: columnKey, direction: SortDirection.asc});
      updateSortIndicators(columnKey, SortDirection.asc, columnKey);
      return {sortSettings: {column: columnKey, direction: SortDirection.asc}};
    });
  }

  openAddDataDialog = () => {
    this.setState({showAddDataDialog: true});
  }

  closeAddDataDialog = () => {
    this.setState({showAddDataDialog: false});
  }

  onAddSuccess = (_) => {
    const {folders} = this.props;
    const id = this.getSelectedFolderId();

    this.props.notify(this.props.intl.formatMessage(MESSAGES.addSubmitted));
    this.props.updateFolders(folders, id ?? "0");
    this.props.clearData();
    this.clearSelection();
  }

  handleRowSelect = (selection: ImportedData[]) => {
    this.setState({selection});
  }

  clearSelectionUpdateFolders = () => {
    const {folders} = this.props;
    const deleted = this.state.selection.filter(f => f.type === "folder");
    if (folders && deleted?.length > 0) {
      deleted.forEach((deletedFolder) => {
        const folder = folders.find(f => f.id === deletedFolder.id);
        if (folder?.parent?.id) {
          this.props.updateFolders(folders, folder.parent.id);
        }
      });
    }
    this.clearSelection();
  }

  clearSelection = () => {
    this.handleRowSelect([]);
  }

  renderCreateFolderButton = () => {
    const {intl} = this.props;
    let tooltip = MESSAGES.folderRoot;

    if (this.isMultipleFoldersSelected() || this.state.selection.length > 1) {
      tooltip = MESSAGES.single;
    } else if (this.isSingleFolderSelectedList()) {
      tooltip = MESSAGES.folderList;
    } else if (this.isNoFoldersSelected() && this.props.filter.folder && this.props.filter.folder !==
               APOLLO_CATALOG_ROOT_FOLDER.id) {
      tooltip = MESSAGES.folderFolderBrowser;
    }

    return <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip">
      {intl.formatMessage(tooltip)}
    </Tooltip>}>
      <div>
        <Button onClick={this.openCreateFolderDialog}
                disabled={this.isMultipleFoldersSelected() || this.state.selection.length > 1}>
          <LcdIcon icon={"plus"}/>
          <FormattedMessage id="studio.data.create-folder" defaultMessage="Create folder"/>
        </Button>
      </div>
    </OverlayTrigger>
  }

  render() {
    const {filter, folders, containerHeight} = this.props;
    const {selection, isFolderBrowserExpanded} = this.state;
    const expandedHeight = containerHeight - 46;
    const browserHeight = expandedHeight - 80;
    const expandedProps = {expanded: true, style: {minHeight: expandedHeight, height: expandedHeight}};
    const collapsedProps = {expanded: false};
    const noSelection = selection.length === 0;

    const masterPane = (
        <Grid fluid={true}>
          <ActionBar>
            {this.renderAddDataButton()}
          </ActionBar>

          <ImportedDataFilter notifyFilterChanged={this.clearSelection}/>

          <ActionBar>
            {this.renderCreateFolderButton()}
            {this.state.isCreateFolderDialogOpen &&
             <CreateFolderDialog parentId={this.getSelectedFolderId()}
                                 onClose={this.closeCreateFolderDialog}
                                 onSubmit={this.onFolderCreated}
                                 api={this.props.api}
                                 intl={this.props.intl}/>}

            <CreateProductButton disabled={noSelection} initialData={selection}/>
            <AddToProductButton disabled={noSelection} initialData={selection}/>
            <PublishDataInServiceButton disabled={noSelection} inputData={selection}/>
            <DeleteDataButton disabled={noSelection} data={selection}
                              onDeleteSuccess={this.isFoldersSelected() ? this.clearSelectionUpdateFolders
                                                                        : this.clearSelection}
                              onDeleteError={Function.prototype}
                              onDeleteWarnings={this.clearSelection}
                              onCloseWarnings={Function.prototype}
                              isFolder={this.isSingleFolderSelectedList()}/>
            {Object.values(this.props.filter).filter(v => v).length > 0 && <div>
              <Button data-cy="clear-filters" onClick={this.clearFilter}>
                <LcdIcon icon="eraser"/>
                <FormattedMessage id="studio.data.clear-filters" defaultMessage="Clear filters"/>
              </Button>
            </div>}
          </ActionBar>

          <div> {/* This empty div is necessary! it's used by ControlRoomTable to calculate its dimensions.*/}
            <ImportedDataList className="user-tour-imported-data-list"
                              onRowSelect={this.handleRowSelect}
                              clearSelection={noSelection}
                              sortSettings={this.state.sortSettings}
                              onSortChange={this.onSortChange}
            />
          </div>

          <DataOutdatedWarning/>
        </Grid>
    );

    const formattedPath = DataListPageComponent.formatPath(getSelectedPath(folders, filter.folder));
    const detailPane = (
        <div>
          <Accordion {...(isFolderBrowserExpanded ? expandedProps : collapsedProps)} data-cy="folder-browser">
            <CustomAccordionSummary data-cy="show-folder-browser"
                                    onClick={this.onAccordionSummaryClick}
                                    expandIcon={<ExpandMoreIcon/>}>
                        <span className="control-label" style={{display: "flex", flex: '0 0 155px'}}>
                            <FormattedMessage id="studio.data.data-list-page.folder-browser"
                                              defaultMessage="FOLDER BROWSER"/>
                        </span>
              {this.props.filter.folder && formattedPath &&
               <CustomChip label={formattedPath}
                           title={formattedPath}
                           size="small"
                           variant="outlined"
                           color="primary"
                           onDelete={this.clearFolderSelection}/>
              }
            </CustomAccordionSummary>
            <AccordionDetails>
              <div style={{
                display: isFolderBrowserExpanded ? 'block' : 'none',
                maxHeight: browserHeight,
                width: '100%'
              }}>
                <DataFolderBrowser notifyFilterChanged={this.clearSelection}/>
              </div>
            </AccordionDetails>
          </Accordion>

          <CustomAccordion {...(isFolderBrowserExpanded ? collapsedProps : expandedProps)}>
            <CustomAccordionSummary onClick={this.onAccordionSummaryClick} expandIcon={<ExpandMoreIcon/>}>
                        <span className="control-label">
                            <FormattedMessage id="studio.data.data-list-page.details" defaultMessage="DETAILS"/>
                        </span>
            </CustomAccordionSummary>
            <AccordionDetails>
              <div style={{
                display: isFolderBrowserExpanded ? 'none' : 'block',
                maxHeight: browserHeight,
                overflow: "auto",
                width: '100%'
              }}>
                <DataListDetailSection
                    selection={selection}
                    api={this.props.api}
                    intl={this.props.intl}/>
              </div>
            </AccordionDetails>
          </CustomAccordion>
        </div>
    );

    return (
        <div>
          <MasterDetailLayout masterPane={masterPane} detailPane={detailPane}/>
          {this.state.showAddDataDialog && <AddDataDialog
              onHide={this.closeAddDataDialog}
              parentFolderId={this.getSelectedFolderId()}
              onSuccess={(r: AddResponse) => this.onAddSuccess(r)}
              api={this.props.api}
              intl={this.props.intl}
          />}
        </div>
    );
  }

  private renderAddDataButton = () => {
    const {intl} = this.props;
    let tooltip = MESSAGES.dataRoot;

    if (this.isMultipleFoldersSelected() || this.state.selection.length > 1) {
      tooltip = MESSAGES.single;
    } else if (this.isSingleFolderSelectedList()) {
      tooltip = MESSAGES.dataList;
    } else if (this.isNoFoldersSelected() && this.props.filter.folder && this.props.filter.folder !==
               APOLLO_CATALOG_ROOT_FOLDER.id) {
      tooltip = MESSAGES.dataFolderBrowser;
    }

    return <div>
      <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip_addData">{intl.formatMessage(tooltip)}</Tooltip>}>
          <span>
            <Button bsStyle="primary" className="actionBarItem"
                    onClick={() => {
                      this.openAddDataDialog();
                      (document.activeElement as HTMLElement).blur(); //make sure tooltip hides
                    }}
                    disabled={this.isMultipleFoldersSelected()}>
             <LcdIcon icon="plus"/>
             <FormattedMessage id="studio.data-list-page.add-data" defaultMessage="Add Data"/>
           </Button>
          </span>
      </OverlayTrigger>
    </div>
  }

  private static formatPath(selectedPath: ApolloCatalogData[]) {
    return selectedPath.map(f => f.title).join("/");
  }

  private openCreateFolderDialog = () => {
    this.setState({isCreateFolderDialogOpen: true});
  };

  private closeCreateFolderDialog = () => {
    this.setState({isCreateFolderDialogOpen: false});
  };

  private onFolderCreated = () => {
    const {folders} = this.props;
    const id = this.getSelectedFolderId();

    this.closeCreateFolderDialog();
    this.props.updateFolders(folders, id ?? "0");
    this.props.clearData();
  }

  private clearFolderSelection = () => {
    const filter = {...this.props.filter};
    delete filter.folder;

    this.clearSelection();
    this.props.filterChanged(filter);
  };

  private clearFilter = () => {
    this.props.clearFilter();
    this.props.clearData();
  };

  private onAccordionSummaryClick = () => {
    localRepository.setOptions(DATA_LIST_PAGE_OPTIONS_KEY,
        {isFolderBrowserExpanded: !this.state.isFolderBrowserExpanded});
    this.setState(prevState => ({isFolderBrowserExpanded: !prevState.isFolderBrowserExpanded}));
  };

  private toggleDataFolderBrowser() {
    this.setState(prevState => ({isDataFolderBrowserOpened: !prevState.isDataFolderBrowserOpened}));
  }

  private isMultipleFoldersSelected() {
    return this.state.selection.filter(f => f.type === "folder").length > 1;
  }

  private isNoFoldersSelected() {
    return this.state.selection.filter(f => f.type === "folder").length === 0;
  }

  private isSingleFolderSelectedList() {
    return this.state.selection.filter(f => f.type === "folder").length === 1;
  }

  private isFoldersSelected() {
    return this.state.selection.filter(f => f.type === "folder").length > 0;
  }

  private getSingleSelectedFolderIdList() {
    if (this.state.selection.length === 1 && this.state.selection[0].type === "folder") {
      return this.state.selection[0].id
    } else {
      return null
    }
  }

  private getSelectedFolderId() {
    const {filter} = this.props;
    const singleSelectedFolderIdList = this.getSingleSelectedFolderIdList();
    if (singleSelectedFolderIdList) {
      return singleSelectedFolderIdList;
    } else if (filter.folder && filter.folder !== APOLLO_CATALOG_ROOT_FOLDER.id) {
      return filter.folder;
    } else {
      return null;
    }
  }

}

const mapStateToProps = (state, _) => {
  return {
    filter: selectors.getFilter(state),
    folderId: selectors.getFilter(state).folder,
    folders: selectors.getFolders(state),
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    notify: (message: string) => {
      dispatch(notificationActions.showInfoNotification(message));
    },
    clearData: () => {
      dispatch(actions.clearData());
    },
    loadFolders: () => {
      dispatch(actions.loadFolders());
    },
    updateFolders: (folders, folderId) => {
      dispatch(actions.updateFolders(folders, folderId));
    },
    clearFilter: () => {
      dispatch(actions.setDataFilter({}));
    },
    filterChanged: (filter: ImportedDataSearchFilter) => {
      dispatch(actions.setDataFilter(filter));
      dispatch(actions.clearData());
    },
  }
}

export const DataListPage = connect(mapStateToProps, mapDispatchToProps)(
    injectIntl(WithApi(Dimensions(fullViewPortDimensions)(DataListPageComponent))));